import React from 'react';
import { Components } from "react-markdown";
import { COVERAGE_STYLES, MD_CAT_BLACK, MD_CAT_YELLOW } from './constants';
import { nodeToText } from './textUtils';
import { getCapabilityNote, setCapabilityNote, useCapabilityImage } from './capabilityStore';
import { CoverageActionButtons } from './components/CoverageActionButtons';

// The model emits raw "<br>" tags inside table cells (Mapped Capability / Supporting
// API columns); HTML isn't enabled so they arrive as literal text. Split them into
// real line breaks, recursing through nested nodes.
const renderWithLineBreaks = (node: React.ReactNode): React.ReactNode => {
  if (typeof node === 'string') {
    const parts = node.split(/<br\s*\/?>/i);
    if (parts.length === 1) return node;
    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {part}
        {i < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => <React.Fragment key={i}>{renderWithLineBreaks(child)}</React.Fragment>);
  }
  return node;
};

const COVERAGE_TOOLTIPS: Record<string, string> = {
  gap:           'No existing API covers this capability — a new or alternative API is needed.',
  partial:       'An API exists but only partially satisfies this capability; gaps remain.',
  covered:       'This capability is fully addressed by the recommended API.',
  'under review': 'Coverage has not been verified yet; awaiting manual confirmation.',
};

const CoverageBadge = ({ label }: { label: string }) => (
  <span
    title={COVERAGE_TOOLTIPS[label.trim().toLowerCase()]}
    style={{
      display: 'inline-block',
      padding: '2px 12px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: 1.6,
      whiteSpace: 'nowrap',
      cursor: 'help',
      ...COVERAGE_STYLES[label.trim().toLowerCase()],
    }}
  >
    {label.trim()}
  </span>
);

// Capability (first) column cell that also renders the AI-generated usage image
// once it has been produced for that capability.
const CapabilityCell = ({ capability, children }: { capability: string; children: React.ReactNode }) => {
  const img = useCapabilityImage(capability);
  return (
    <td style={{ border: '1px solid #ececec', padding: '8px 10px', verticalAlign: 'top', textAlign: 'left', lineHeight: 1.45 }}>
      {children}
      {img && (
        <img
          src={img}
          alt={`${capability} usage`}
          title="AI-generated usage illustration (click to open)"
          onClick={() => window.open(img, '_blank')}
          style={{ display: 'block', maxWidth: '100%', marginTop: '8px', borderRadius: '6px', border: '1px solid #ececec', cursor: 'zoom-in' }}
        />
      )}
    </td>
  );
};

// Editable Notes cell. Seeds from the user's saved note (if any), otherwise from
// the model-generated note text, and writes every edit back to the notes store.
const NotesCell = ({ capability, initial }: { capability: string; initial: string }) => {
  const [value, setValue] = React.useState<string>(() => getCapabilityNote(capability) ?? initial);
  const taRef = React.useRef<HTMLTextAreaElement>(null);

  // Height=0 forces scrollHeight to reflect full content before we size up.
  const autoSize = React.useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = '0';
    ta.style.height = `${ta.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => { autoSize(); }, [autoSize, value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    setCapabilityNote(capability, e.target.value);
  };

  return (
    <td style={{ border: '1px solid #ececec', padding: '6px 8px', verticalAlign: 'top', textAlign: 'left', lineHeight: 1.45 }}>
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        placeholder="Add notes..."
        aria-label={`Notes for ${capability}`}
        rows={1}
        style={{
          width: '100%',
          minHeight: '28px',
          resize: 'none',
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          padding: '4px 8px',
          fontSize: '12px',
          fontFamily: 'inherit',
          color: MD_CAT_BLACK,
          background: 'transparent',
          boxSizing: 'border-box',
          outline: 'none',
        }}
        onFocus={(e) => { e.currentTarget.style.border = `1px solid ${MD_CAT_YELLOW}`; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255,205,17,0.35)'; }}
        onBlur={(e) => { e.currentTarget.style.border = '1px solid #e0e0e0'; e.currentTarget.style.boxShadow = 'none'; }}
      />
    </td>
  );
};

export const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '12px 0 8px', color: MD_CAT_BLACK }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: '17px', fontWeight: 700, margin: '14px 0 8px', paddingBottom: '4px', borderBottom: `2px solid ${MD_CAT_YELLOW}`, color: MD_CAT_BLACK }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '12px 0 6px', color: MD_CAT_BLACK }}>{children}</h3>
  ),
  p: ({ children }) => (
    <p style={{ margin: '0 0 8px', lineHeight: 1.5, textAlign: 'left' }}>{children}</p>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: '18px', margin: '0 0 8px' }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: '18px', margin: '0 0 8px' }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: '3px', lineHeight: 1.45 }}>{children}</li>
  ),
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{children}</a>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ backgroundColor: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9em' }}>{children}</code>
    ) : (
      <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '6px', overflowX: 'auto', margin: '0 0 8px' }}>
        <code style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{children}</code>
      </pre>
    ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '4px 0 14px', border: '1px solid #ececec', borderRadius: '8px' }}>
      <table className="rcb-md-table" style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', fontSize: '13px' }}>{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ backgroundColor: MD_CAT_YELLOW }}>{children}</thead>
  ),
  th: ({ children }) => (
    <th style={{ border: '1px solid #ececec', padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: MD_CAT_BLACK }}>{children}</th>
  ),
  td: ({ children }) => (
    <td style={{ border: '1px solid #ececec', padding: '8px 10px', verticalAlign: 'top', textAlign: 'left', lineHeight: 1.45 }}>{renderWithLineBreaks(children)}</td>
  ),
  tr: ({ children }) => {
    const cells = React.Children.toArray(children);
    let coverageIdx = -1;
    let coverageText = '';
    cells.forEach((cell, i) => {
      const raw = nodeToText(cell).trim();
      const key = raw.toLowerCase();
      if (key === 'gap' || key === 'partial' || key === 'covered' || key === 'under review') {
        coverageIdx = i;
        coverageText = raw;
      }
    });

    // Not a coverage row (e.g. header or plain table) -> render untouched.
    if (coverageIdx === -1) {
      return <tr>{children}</tr>;
    }

    const capability = nodeToText(cells[0]).trim();
    const notesIdx = cells.length - 1;
    // The capability table has an Open Queries column after Coverage; the acceptance-
    // criteria table ends at its Status column. Only the former gets recheck actions.
    const coverageKey = coverageText.toLowerCase();
    const showActions = coverageIdx !== notesIdx && (coverageKey === 'gap' || coverageKey === 'partial');
    const enhanced = cells.map((cell, i) => {
      if (i === 0) {
        const el = cell as React.ReactElement<{ children?: React.ReactNode }>;
        return (
          <CapabilityCell key="cap" capability={capability}>
            {el?.props?.children}
          </CapabilityCell>
        );
      }
      if (i === coverageIdx) {
        return (
          <td key="coverage" style={{ border: '1px solid #ececec', padding: '8px 10px', verticalAlign: 'top', textAlign: 'center', lineHeight: 1.45 }}>
            <CoverageBadge label={coverageText} />
            {showActions && <CoverageActionButtons capability={capability} coverage={coverageKey as 'gap' | 'partial'} />}
          </td>
        );
      }
      // Last column is the Open Queries column -> render an editable text box.
      if (i === notesIdx && notesIdx !== 0 && notesIdx !== coverageIdx) {
        return <NotesCell key="open-queries" capability={capability} initial={nodeToText(cell).trim()} />;
      }
      return cell;
    });
    return <tr>{enhanced}</tr>;
  },
};
